/**
 * SDTestApp browser bridge — impersonates the Manufacturing-Dashboard (Appsmith) `sdTest` JSObject.
 *
 * SDTestApp is a local desktop app that serves an HTTPS page on `https://127.0.0.1:<port>` (default 8088).
 * It is **never** called over fetch/HTTP: Appsmith embeds it as an iframe and talks to it with
 * `window.postMessage` plus an SDTestApp-initiated handshake. This bridge reproduces that exact protocol so the
 * hub Configuration page behaves identically (see `docs/sdtestapp-and-wireless-portal-contract.md`).
 *
 * Because this is postMessage (not fetch), CORS does not apply. The only browser requirements are:
 *  - the SDTestApp self-signed TLS cert must be trusted, and
 *  - SDTestApp must allow being framed by / posting to the hub origin.
 */

import { resolveDeviceLabelSerialFromCandidates } from "./deviceLabelSerial";

export const SDTESTAPP_DEFAULT_PORT = "8088";

export type SdTestConnectionStatus = "connecting" | "connected" | "disconnected";

export interface SdTestDevice {
  connectorId?: string;
  cableSerialNumber?: string;
  serialNumber?: string;
  modelNumber?: string;
  firmwareVersion?: string;
  [key: string]: unknown;
}

export interface SdTestDemographics {
  devices: SdTestDevice[];
  deviceCount?: number;
  status?: string;
}

export interface SdTestStatus {
  text: string;
  color: string;
}

export interface ConfigureResult {
  success: boolean;
  firmware: string;
  response: Record<string, unknown>;
}

export interface SdTestBridgeListeners {
  onStatusChange?: (status: SdTestStatus, connectionStatus: SdTestConnectionStatus) => void;
  onDemographics?: (demographics: SdTestDemographics | null) => void;
}

const STATUS_OFFLINE: SdTestStatus = { text: "SDTestApp Offline", color: "#DC3545" };
const STATUS_CONNECTED: SdTestStatus = { text: "SDTestApp Connected", color: "#28A745" };

function generateRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function normalizeError(raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "string") return raw.trim();
  if (raw instanceof Error) return raw.message;
  if (typeof raw === "object") {
    const candidate = raw as Record<string, unknown>;
    const message = candidate.message ?? candidate.error ?? candidate.detail;
    if (typeof message === "string") return message.trim();
    try {
      return JSON.stringify(raw);
    } catch {
      return String(raw);
    }
  }
  return String(raw);
}

/** True when the connector is the debug DUMMY / DUMMY-PORT session. */
export function isSdTestDummyDevice(device: SdTestDevice | undefined | null): boolean {
  if (!device) return false;
  return String(device.connectorId ?? "").toLowerCase().includes("dummy");
}

/** All devices from a demographics snapshot, including debug dummy. */
export function listSdTestDevices(demographics: SdTestDemographics | null | undefined): SdTestDevice[] {
  const devices = demographics?.devices ?? [];
  return Array.isArray(devices) ? devices : [];
}

/**
 * Devices considered for single-device-only configuration.
 * Prefer non-dummy when any real device is present; otherwise keep dummy so
 * debug-only setups still configure. Never strip dummy from the raw snapshot.
 */
export function devicesForSingleDeviceConfig(devices: SdTestDevice[]): SdTestDevice[] {
  const list = Array.isArray(devices) ? devices : [];
  const nonDummy = list.filter((device) => !isSdTestDummyDevice(device));
  return nonDummy.length > 0 ? nonDummy : list;
}

/**
 * Manages a single SDTestApp iframe connection. Construct with the target port, attach the iframe element via
 * `attach()`, then call `configureAndWait()` when a device is connected. Always `dispose()` on unmount.
 */
export class SdTestAppBridge {
  private port: string;
  private iframe: HTMLIFrameElement | null = null;
  private listeners: SdTestBridgeListeners;

  private connectionStatus: SdTestConnectionStatus = "connecting";
  private status: SdTestStatus = STATUS_OFFLINE;
  private lastDemographics: SdTestDemographics | null = null;

  private messageHandler: ((event: MessageEvent) => void) | null = null;

  private pendingConfigure:
    | {
        resolve: (result: ConfigureResult) => void;
        reject: (error: Error) => void;
        connectorId: string;
        modelNumber: string;
        requestId: string;
        timeoutId: ReturnType<typeof setTimeout>;
        successReceived: boolean;
      }
    | null = null;

  constructor(port: string = SDTESTAPP_DEFAULT_PORT, listeners: SdTestBridgeListeners = {}) {
    this.port = String(port || SDTESTAPP_DEFAULT_PORT).trim();
    this.listeners = listeners;
  }

  /** The origin the iframe is served from; postMessage target and inbound origin allowlist. */
  get targetOrigin(): string {
    return `https://127.0.0.1:${this.port}`;
  }

  get iframeUrl(): string {
    return `${this.targetOrigin}?embed=hub&t=${Date.now()}`;
  }

  getStatus(): SdTestStatus {
    return this.status;
  }

  getConnectionStatus(): SdTestConnectionStatus {
    return this.connectionStatus;
  }

  /**
   * Devices for single-device-only configuration (prefers non-dummy when both
   * are present). Raw demographics still include dummy via onDemographics.
   */
  getActiveDevices(): SdTestDevice[] {
    return devicesForSingleDeviceConfig(listSdTestDevices(this.lastDemographics));
  }

  /** Attaches the iframe element and starts listening for SDTestApp messages. */
  attach(iframe: HTMLIFrameElement): void {
    this.iframe = iframe;
    if (!this.messageHandler) {
      this.messageHandler = (event: MessageEvent) => this.handleWindowMessage(event);
      window.addEventListener("message", this.messageHandler);
    }
    // Proactively ping; SDTestApp also initiates a handshake on load.
    this.ping();
  }

  /** Reloads the iframe (use when SDTestApp started after the page). */
  reload(): void {
    if (this.iframe) {
      this.iframe.src = this.iframeUrl;
    }
  }

  dispose(): void {
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
      this.messageHandler = null;
    }
    this.clearPendingConfigure();
    this.iframe = null;
  }

  private postMessage(message: Record<string, unknown>): string {
    const requestId = (message.requestId as string) ?? generateRequestId();
    const payload = { ...message, requestId };
    const target = this.iframe?.contentWindow;
    if (!target) {
      return requestId;
    }
    target.postMessage(payload, this.targetOrigin);
    return requestId;
  }

  ping(): void {
    this.postMessage({ action: "ping" });
  }

  private setStatus(status: SdTestStatus): void {
    this.status = status;
    this.listeners.onStatusChange?.(status, this.connectionStatus);
  }

  private markDisconnected(): void {
    this.connectionStatus = "disconnected";
    this.lastDemographics = null;
    this.listeners.onDemographics?.(null);
    this.setStatus(STATUS_OFFLINE);
  }

  private handleWindowMessage(event: MessageEvent): void {
    // Only accept messages from the SDTestApp iframe origin + window.
    if (event.origin !== this.targetOrigin) return;
    if (this.iframe && event.source !== this.iframe.contentWindow) return;

    const wrapper = event.data as Record<string, unknown> | undefined;
    const message = ((wrapper?.message as Record<string, unknown>) ?? wrapper) as
      | Record<string, unknown>
      | undefined;
    if (!message) return;
    if (message.command === "calculateSubFramePositioning") return;
    if (!this.isValidMessage(message) && !this.isValidMessage(wrapper)) return;

    if (message.action === "disconnect") {
      this.markDisconnected();
      return;
    }

    if (message.action === "sdtestappHandshakeRequest") {
      const response: Record<string, unknown> = {
        action: "appsmithHandshake",
        status: "ack",
        handshakeSessionId: (message.handshakeSessionId as string) ?? "none",
      };
      if (message.clientId !== undefined) {
        response.clientId = message.clientId;
      }
      this.postMessage(response);
      return;
    }

    if (this.connectionStatus !== "connected") {
      this.connectionStatus = "connected";
    }

    // Reject pending configure if a non-configure error rides along.
    this.tryRejectFromErrors(message, wrapper);

    const demographics = this.extractDemographics(message, wrapper);
    if (demographics) {
      this.lastDemographics = demographics;
      this.listeners.onDemographics?.(this.lastDemographics);
    }

    this.processConfigureResults(message, wrapper);
    if (this.lastDemographics) {
      const demographicsRecord = this.lastDemographics as unknown as Record<string, unknown>;
      this.tryResolveConfigure(demographicsRecord, demographicsRecord);
    }

    this.updateStatus(demographics ?? message);

    if (message.error && this.pendingConfigure == null) {
      // surface only; no global error sink in the hub
    }
  }

  private isValidMessage(message: Record<string, unknown> | undefined): boolean {
    if (!message || typeof message !== "object") return false;
    if (message.command === "calculateSubFramePositioning") return false;
    const data = message.data as Record<string, unknown> | undefined;
    const hasDevices = message.devices !== undefined || data?.devices !== undefined;
    const hasSideChannel =
      Array.isArray(message.commandResults) || Array.isArray(data?.commandResults);
    return (
      hasDevices ||
      hasSideChannel ||
      message.status !== undefined ||
      message.action !== undefined ||
      message.error !== undefined ||
      message.deviceCount !== undefined
    );
  }

  private mergedCandidates(
    message: Record<string, unknown> | undefined,
    wrapper: Record<string, unknown> | undefined,
  ): Record<string, unknown>[] {
    const raw = [
      message,
      wrapper,
      wrapper?.message as Record<string, unknown> | undefined,
      message?.data as Record<string, unknown> | undefined,
      wrapper?.data as Record<string, unknown> | undefined,
    ];
    return raw.filter(
      (m, index, arr): m is Record<string, unknown> =>
        !!m && typeof m === "object" && arr.indexOf(m) === index,
    );
  }

  private extractDemographics(
    message: Record<string, unknown> | undefined,
    wrapper: Record<string, unknown> | undefined,
  ): SdTestDemographics | null {
    const candidates = [
      message,
      message?.data as Record<string, unknown> | undefined,
      wrapper,
      wrapper?.message as Record<string, unknown> | undefined,
      wrapper?.data as Record<string, unknown> | undefined,
    ];
    for (const candidate of candidates) {
      if (candidate && Array.isArray((candidate as Record<string, unknown>).devices)) {
        return { ...(candidate as unknown as SdTestDemographics) };
      }
    }
    return null;
  }

  private updateStatus(message: Record<string, unknown> | SdTestDemographics | undefined): void {
    if (this.connectionStatus === "disconnected") {
      this.setStatus(STATUS_OFFLINE);
      return;
    }
    const activeCount = this.getActiveDevices().length;
    const record = message as Record<string, unknown> | undefined;
    if (record?.error) {
      this.setStatus({ text: "SDTestApp Error", color: "#DC3545" });
      return;
    }
    if (activeCount === 1) {
      this.setStatus(STATUS_CONNECTED);
    } else if (activeCount === 0) {
      this.setStatus({ text: "SDTestApp Connected (No Active Devices)", color: "#FFC107" });
    } else {
      this.setStatus({ text: `SDTestApp Too Many Devices (${activeCount})`, color: "#DC3545" });
    }
  }

  // ----- configure -----

  configure(connectorId: string, modelNumber: string): string {
    return this.postMessage({ action: "configure", modelNumber, target: connectorId });
  }

  /**
   * Sends `configure` and resolves once SDTestApp reports success and firmware is available.
   * Mirrors `sdTest.configureAndWait`.
   */
  configureAndWait(connectorId: string, modelNumber: string, timeoutMs = 60000): Promise<ConfigureResult> {
    this.clearPendingConfigure();
    return new Promise<ConfigureResult>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error("Configuration timeout: No response from sdtestapp within timeout period"));
        this.clearPendingConfigure();
      }, timeoutMs);
      const requestId = this.configure(connectorId, modelNumber);
      this.pendingConfigure = {
        resolve,
        reject,
        connectorId,
        modelNumber,
        requestId,
        timeoutId,
        successReceived: false,
      };
    });
  }

  private clearPendingConfigure(): void {
    if (this.pendingConfigure) {
      clearTimeout(this.pendingConfigure.timeoutId);
    }
    this.pendingConfigure = null;
  }

  private modelNumberMatches(pending: string | null, candidate: unknown): boolean {
    if (pending == null || candidate == null) return false;
    return String(pending).trim().toUpperCase() === String(candidate).trim().toUpperCase();
  }

  private isConfigureForUs(response: Record<string, unknown>): boolean {
    if (!response || response.action !== "configure") return false;
    const pending = this.pendingConfigure;
    if (!pending) return false;
    const hasRequestId = response.requestId != null && String(response.requestId).trim() !== "";
    if (hasRequestId) {
      return String(response.requestId).trim() === String(pending.requestId).trim();
    }
    return this.modelNumberMatches(pending.modelNumber, response.modelNumber);
  }

  private findDevice(devices: SdTestDevice[]): SdTestDevice | null {
    const pending = this.pendingConfigure;
    const all = Array.isArray(devices) ? devices : [];
    if (pending) {
      const match = all.find(
        (device) =>
          device.connectorId === pending.connectorId ||
          device.cableSerialNumber === pending.connectorId ||
          device.serialNumber === pending.connectorId ||
          device.modelNumber === pending.modelNumber,
      );
      if (match) return match;
    }
    const preferred = devicesForSingleDeviceConfig(all);
    return preferred.length === 1 ? preferred[0] : null;
  }

  private tryRejectFromErrors(
    message: Record<string, unknown> | undefined,
    wrapper: Record<string, unknown> | undefined,
  ): void {
    const pending = this.pendingConfigure;
    if (!pending) return;
    if (message?.error !== undefined && message?.error !== null) {
      const text = normalizeError(message.error);
      if (text && !text.toLowerCase().includes("unknown action")) {
        pending.reject(new Error(text));
        this.clearPendingConfigure();
        return;
      }
    }
    for (const candidate of this.mergedCandidates(message, wrapper)) {
      const results = candidate.commandResults;
      if (!Array.isArray(results)) continue;
      for (const item of results as Record<string, unknown>[]) {
        if (!item || typeof item !== "object") continue;
        if (item.action === "configure") continue;
        if (item.error === undefined || item.error === null) continue;
        const text = normalizeError(item.error);
        if (text && !text.toLowerCase().includes("unknown action")) {
          pending.reject(new Error(text));
          this.clearPendingConfigure();
          return;
        }
      }
    }
  }

  private processConfigureResults(
    message: Record<string, unknown> | undefined,
    wrapper: Record<string, unknown> | undefined,
  ): void {
    for (const candidate of this.mergedCandidates(message, wrapper)) {
      if (candidate.action === "configure" && this.isConfigureForUs(candidate)) {
        this.tryResolveConfigure(candidate, candidate);
      }
      const results = candidate.commandResults;
      if (!Array.isArray(results)) continue;
      for (const item of results as Record<string, unknown>[]) {
        if (item && this.isConfigureForUs(item)) {
          this.tryResolveConfigure(item, candidate);
        }
      }
    }
  }

  private tryResolveConfigure(
    message: Record<string, unknown>,
    merged: Record<string, unknown>,
  ): void {
    const pending = this.pendingConfigure;
    if (!pending) return;

    const devicesFromMerged =
      (merged.devices as SdTestDevice[] | undefined) ??
      ((merged.data as Record<string, unknown> | undefined)?.devices as SdTestDevice[] | undefined) ??
      [];
    const devicesFromLast = this.lastDemographics?.devices ?? [];
    const devices =
      Array.isArray(devicesFromMerged) && devicesFromMerged.length > 0
        ? devicesFromMerged
        : devicesFromLast;
    const device = this.findDevice(devices);
    const firmware = device?.firmwareVersion;

    if (message.action === "configure" && this.modelNumberMatches(pending.modelNumber, message.modelNumber)) {
      const errorCount = Number(message.errorCount) || 0;
      const errors = Array.isArray(message.errors) ? (message.errors as unknown[]) : [];
      if (errorCount > 0 || errors.length > 0) {
        const joined = errors.map((entry) => normalizeError(entry)).filter(Boolean).join(", ");
        pending.reject(new Error(joined || "Configuration failed"));
        this.clearPendingConfigure();
        return;
      }
      if (Number(message.successCount) > 0) {
        pending.successReceived = true;
        if (firmware) {
          pending.resolve({ success: true, firmware, response: message });
          this.clearPendingConfigure();
          return;
        }
      }
    }

    if (message.devices && pending.successReceived) {
      const device2 = this.findDevice(message.devices as SdTestDevice[]);
      const firmware2 = device2?.firmwareVersion || firmware;
      if (firmware2) {
        pending.resolve({ success: true, firmware: firmware2, response: { action: "configure", successCount: 1 } });
        this.clearPendingConfigure();
      }
    }
  }
}

/** SDTestApp field-name variants for values read off device demographics (mirrors configure.js). */
export const SDTEST_FIELD_KEYS = {
  mcuUuid: ["mcuuuid", "mcuUuid", "mcu_id", "mcuId", "deviceId", "device_id", "uuid"],
  imei: ["imei", "IMEI", "imeiNumber", "imei_number"],
  iccid: ["iccid", "ICCID"],
  pcbaSerial: ["pcbaSerial", "pcba_serial", "pcbaSerialNumber", "pcbaSerialNo"],
  clampSerial: ["clampSerial", "clamp_serial", "clampSerialNumber"],
  deviceLabelSerial: ["deviceLabelSerial", "device_label_serial", "deviceLabel", "labelSerial", "fciSerialNumber"],
} as const;

/** Reads the first non-empty value among `keys` on a device object. */
export function readDeviceField(device: SdTestDevice | null | undefined, keys: readonly string[]): string {
  if (!device) return "";
  for (const key of keys) {
    const raw = device[key];
    if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
      return String(raw).trim();
    }
  }
  return "";
}

/** Prefer the full printed label over SDTestApp's bare seven-digit `fciSerialNumber` suffix. */
export function readDeviceLabelSerial(device: SdTestDevice | null | undefined): string {
  if (!device) return "";
  const candidates = SDTEST_FIELD_KEYS.deviceLabelSerial.map((key) => {
    const raw = device[key];
    return raw === undefined || raw === null ? "" : String(raw).trim();
  });
  return resolveDeviceLabelSerialFromCandidates(candidates);
}

/** Picks the MCU UUID from a configure response or device (mirrors configure.extractMcuId). */
export function extractMcuId(
  configureResponse: Record<string, unknown> | null | undefined,
  device: SdTestDevice | null | undefined,
): string {
  const response = configureResponse ?? {};
  const fromResponse = SDTEST_FIELD_KEYS.mcuUuid.map((key) => response[key]);
  const fromDevice = device ? SDTEST_FIELD_KEYS.mcuUuid.map((key) => device[key]) : [];
  const found = [...fromResponse, ...fromDevice].find((value) => String(value ?? "").trim() !== "");
  return found ? String(found).trim() : "";
}
